import { useMemo, useState } from "react";
import { Ban, CheckCircle2, Edit, ShieldOff, Users } from "lucide-react";
import { Reactable } from "../../components/ui/reactable";
import { PAYROLL_HOLD } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import { DialogModal } from "../../components/ui/dialogModal";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/input";
import Checkbox from "antd/es/checkbox/Checkbox";
import { Select } from "antd";
import { salaryTypeOptions } from "../../constants/enum";
import { useOrgAdminBranchColumn } from "../../hooks/useOrgAdminBranchColumn";

/**
 * Payroll Hold — employees excluded from the next payroll run when hold is ON.
 * HR can toggle hold status per employee or in bulk from this screen.
 */
const PayrollHoldEmployees = () => {
    const { update } = useApiMethods();
    const { edit: editPermission } = checkUserPermissionFunc("payroll", "Payroll Hold", "SubMenu");

    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [salaryTypeFilter, setSalaryTypeFilter] = useState("all");
    const [holdStatus, setHoldStatus] = useState(false);
    const [holdRemarks, setHoldRemarks] = useState("");
    const [dialogMode, setDialogMode] = useState("single");
    const [bulkHold, setBulkHold] = useState(true);
    const [selected, setSelected] = useState(() => new Set());

    // Reactable hashes the filter object, so changing the value refetches on its
    // own -- no serverTrigger flip needed.
    const { insertBranchColumn } = useOrgAdminBranchColumn("employee_name");
    const salaryFilter = useMemo(() => ({ salary_type: salaryTypeFilter }), [salaryTypeFilter]);
    const salaryTypeLabel = (value) =>
        salaryTypeOptions.find((option) => option.value === value)?.label || "-";

    const pageIds = useMemo(() => data.map((row) => row.id).filter(Boolean), [data]);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
    const somePageSelected = pageIds.some((id) => selected.has(id));

    const holdBeingEnabled = dialogMode === "bulk" ? bulkHold : holdStatus;
    const remarksRequired = holdBeingEnabled;

    const resetForm = () => {
        setOpenDialog(false);
        setSelectedRow(null);
        setHoldStatus(false);
        setHoldRemarks("");
        setDialogMode("single");
        setBulkHold(true);
    };

    const toggleRow = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAllOnPage = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allPageSelected) {
                pageIds.forEach((id) => next.delete(id));
            } else {
                pageIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    const clearSelection = () => setSelected(new Set());

    const handleOpenEdit = (row) => {
        setDialogMode("single");
        setSelectedRow(row);
        setHoldStatus(!!row?.is_payroll_hold);
        setHoldRemarks(row?.hold_remarks || row?.payroll_hold_remarks || "");
        setOpenDialog(true);
    };

    const handleOpenBulk = (hold) => {
        if (!selected.size) return;
        setDialogMode("bulk");
        setBulkHold(hold);
        setSelectedRow(null);
        setHoldRemarks("");
        setOpenDialog(true);
    };

    const refreshTable = () => {
        clearSelection();
        setServerTrigger((prev) => !prev);
    };

    const validateRemarks = () => {
        if (remarksRequired && !holdRemarks.trim()) {
            ToastMsg("error", "Please enter remarks for putting employee(s) on payroll hold");
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateRemarks()) return;

        if (dialogMode === "bulk") {
            try {
                setLoading(true);
                const remarks = holdRemarks.trim();
                const response = await update(PAYROLL_HOLD, {
                    remarks: remarks || undefined,
                    items: [...selected].map((id) => ({
                        id,
                        is_payroll_hold: bulkHold,
                    })),
                });
                if (response?.status === "success") {
                    ToastMsg(
                        "success",
                        response.message || `${selected.size} employee(s) ${bulkHold ? "put on hold" : "removed from hold"}`,
                    );
                    resetForm();
                    refreshTable();
                } else {
                    ToastMsg("error", response?.message || "Failed to update payroll hold");
                }
            } catch (error) {
                console.error(error);
                ToastMsg("error", "Failed to update payroll hold");
            } finally {
                setLoading(false);
            }
            return;
        }

        if (!selectedRow?.id) return;
        try {
            setLoading(true);
            const response = await update(PAYROLL_HOLD, {
                id: selectedRow.id,
                is_payroll_hold: holdStatus,
                remarks: holdRemarks.trim() || undefined,
            });
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Payroll hold updated");
                resetForm();
                refreshTable();
            } else {
                ToastMsg("error", response?.message || "Failed to update payroll hold");
            }
        } catch (error) {
            console.error(error);
            ToastMsg("error", "Failed to update payroll hold");
        } finally {
            setLoading(false);
        }
    };

    const dialogTitle = dialogMode === "bulk"
        ? (bulkHold ? "Put Employees on Payroll Hold" : "Remove Payroll Hold")
        : "Update Payroll Hold";

    const dialogSaveLabel = dialogMode === "bulk"
        ? (bulkHold ? "Put on Hold" : "Remove Hold")
        : "Save";

    const columns = insertBranchColumn([
        ...(editPermission ? [{
            accessor: "_select",
            Header: (
                <input
                    type="checkbox"
                    checked={allPageSelected}
                    ref={(el) => {
                        if (el) el.indeterminate = somePageSelected && !allPageSelected;
                    }}
                    onChange={toggleAllOnPage}
                    className="rounded border-gray-300 text-sidebar-primary focus:ring-sidebar-primary/30 cursor-pointer"
                    aria-label="Select all on page"
                />
            ),
            sortable: false,
            width: "44px",
            Cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={selected.has(row.id)}
                    onChange={() => toggleRow(row.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-gray-300 text-sidebar-primary focus:ring-sidebar-primary/30 cursor-pointer"
                    aria-label={`Select ${row.employee_name || row.employee_id}`}
                />
            ),
        }] : []),
        {
            accessor: "employee_id",
            Header: "Employee ID",
            sortable: true,
            filterable: true,
            fixed: true,
        },
        {
            accessor: "employee_name",
            Header: "Employee Name",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => value?.trim() || "-",
        },
        {
            accessor: "department_name",
            Header: "Department",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "employee_type_name",
            Header: "Employee Type",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "date_of_joining",
            Header: "Date of Joining",
            sortable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "salary_type",
            Header: "Salary Type",
            sortable: true,
            Cell: ({ value }) => salaryTypeLabel(value),
        },
        {
            accessor: "is_payroll_hold",
            Header: "Payroll Hold",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full",
                        value
                            ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                            : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                    )}
                >
                    {value ? (
                        <>
                            <Ban className="h-3 w-3" /> On Hold
                        </>
                    ) : (
                        "Active"
                    )}
                </span>
            ),
        },
        {
            accessor: "hold_remarks",
            Header: "Remarks",
            sortable: false,
            filterable: false,
            Cell: ({ value, row }) => (
                row?.is_payroll_hold && value?.trim()
                    ? (
                        <span className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2" title={value}>
                            {value}
                        </span>
                    )
                    : "-"
            ),
        },
        ...(editPermission ? [{
            accessor: "action",
            Header: "Actions",
            sortable: false,
            Cell: ({ row }) => (
                <Button
                    variant="ghost"
                    size="sm"
                    className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                    onClick={() => handleOpenEdit(row)}
                >
                    <Edit className="h-4 w-4" />
                </Button>
            ),
        }] : []),
    ]);

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={ShieldOff}
                title="Payroll Hold Employees"
                subtitle="Employees on payroll hold are skipped when payroll is processed"
                totalRecords={totalRecords}
            />

            <div className="flex flex-wrap items-center gap-2 mb-3 px-1">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Salary Type
                </label>
                <Select
                    size="middle"
                    value={salaryTypeFilter}
                    onChange={(value) => setSalaryTypeFilter(value || "all")}
                    options={[
                        { label: "All", value: "all" },
                        ...salaryTypeOptions,
                        { label: "Not set", value: "not_set" },
                    ]}
                    className="w-44"
                />
            </div>

            {editPermission && (
                <div className="flex flex-wrap items-center gap-3 mb-3 px-1">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {selected.size} selected
                    </span>
                    <Button
                        type="button"
                        size="sm"
                        disabled={!selected.size || loading}
                        onClick={() => handleOpenBulk(true)}
                        className="gap-1.5 bg-rose-600 hover:bg-rose-700 text-white"
                    >
                        <Ban className="h-3.5 w-3.5" />
                        Put on Hold
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!selected.size || loading}
                        onClick={() => handleOpenBulk(false)}
                        className="gap-1.5"
                    >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Remove Hold
                    </Button>
                    {selected.size > 0 && (
                        <button
                            type="button"
                            onClick={clearSelection}
                            className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                            Clear selection
                        </button>
                    )}
                    {!selected.size && (
                        <span className="text-xs text-gray-400">Select rows to enable bulk update</span>
                    )}
                </div>
            )}

            <Reactable
                columns={columns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                url={PAYROLL_HOLD}
                serverSide={true}
                serverTrigger={serverTrigger}
                filter={salaryFilter}
                rowSize={editPermission ? "sm" : null}
                isCSVRequired={true}
                isPDFRequired={true}
                isDateRequired={false}
            />

            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onSave={handleSave}
                title={dialogTitle}
                closeLabel="Cancel"
                saveLabel={dialogSaveLabel}
                editMode={true}
                loading={loading}
                saveButtonColor={dialogMode === "bulk" && bulkHold ? "destructive" : undefined}
                width="440px"
            >
                <div className="space-y-4">
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/50">
                        {dialogMode === "bulk" ? (
                            <>
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                                    <Users className="h-4 w-4 text-sidebar-primary shrink-0" />
                                    {selected.size} employee{selected.size === 1 ? "" : "s"} selected
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {bulkHold
                                        ? "These employees will be excluded from the next payroll run."
                                        : "These employees will be included in payroll again."}
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {selectedRow?.employee_name || "-"}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {selectedRow?.employee_id || "-"} · {selectedRow?.department_name || "-"}
                                </p>
                            </>
                        )}
                    </div>

                    {dialogMode === "single" && (
                        <Checkbox
                            checked={holdStatus}
                            onChange={(e) => setHoldStatus(e.target.checked)}
                        >
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                Hold payroll for this employee
                            </span>
                        </Checkbox>
                    )}

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            Remarks{remarksRequired ? " *" : " (optional)"}
                        </label>
                        <Textarea
                            value={holdRemarks}
                            onChange={(e) => setHoldRemarks(e.target.value)}
                            rows={3}
                            placeholder={
                                holdBeingEnabled
                                    ? "e.g. Pending exit clearance, salary dispute, document pending..."
                                    : "Optional note for removing hold..."
                            }
                        />
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {holdBeingEnabled
                            ? "Remarks are required when putting on hold and are recorded in the employee activity log."
                            : "When enabled, employees are excluded from payroll processing until hold is removed."}
                    </p>
                </div>
            </DialogModal>
        </ContainerCard>
    );
};

export default PayrollHoldEmployees;
